/**
 * Report Generate Page
 * Allows users to generate a report from a template with AI content and live preview
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBusinessPlan } from '../../App';
import {
  ArrowLeft,
  FileText,
  Download,
  Eye,
  Loader2,
  CheckCircle,
  AlertCircle,
  FileSpreadsheet,
  Sparkles,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  BarChart2,
  TableProperties,
  Calendar,
  Settings
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { PDFGenerator, DOCXGenerator } from './services/ReportGenerator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReportGeneratePage = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { currentPlan } = useBusinessPlan();
  
  const [template, setTemplate] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [exportFormat, setExportFormat] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedBlocks, setExpandedBlocks] = useState({});
  const [configStep, setConfigStep] = useState(true); // Start with config step
  
  // Time Period Configuration (set BEFORE generating report)
  const [periodConfig, setPeriodConfig] = useState({
    period_type: 'monthly',
    mode: 'calendar',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0]
  });

  // Fetch template details only (not generating yet)
  const fetchTemplate = async () => {
    if (!templateId || !token) return;

    setLoading(true);
    setError(null);

    try {
      const templateRes = await fetch(
        `${BACKEND_URL}/api/report-templates/${templateId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!templateRes.ok) {
        throw new Error('Template not found');
      }
      
      const templateData = await templateRes.json();
      setTemplate(templateData.template);
    } catch (err) {
      console.error('Error fetching template:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Generate report with configured settings
  const generateReport = async () => {
    if (!templateId || !token || !currentPlan?.id) {
      setError('Please select a business plan first');
      return;
    }

    setGenerating(true);
    setError(null);
    setConfigStep(false); // Move to preview step

    try {
      // Generate report data with AI content using configured period
      const generateRes = await fetch(
        `${BACKEND_URL}/api/report-templates/generate/${templateId}?plan_id=${currentPlan.id}&format=pdf`,
        {
          method: 'POST',
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ period_config: periodConfig })
        }
      );

      if (!generateRes.ok) {
        const errData = await generateRes.json();
        throw new Error(errData.detail || 'Failed to generate report');
      }

      const report = await generateRes.json();
      setReportData(report);
      
      // Auto-expand blocks with AI content for preview
      const expanded = {};
      report.blocks?.forEach((block, idx) => {
        if (block.ai_content) {
          expanded[idx] = true;
        }
      });
      setExpandedBlocks(expanded);
    } catch (err) {
      console.error('Error generating report:', err);
      setError(err.message);
      setConfigStep(true); // Go back to config on error
    } finally {
      setGenerating(false);
    }
  };

  // Legacy fetchData for regeneration
  const fetchData = async () => {
    await generateReport();
  };

  useEffect(() => {
    fetchTemplate(); // Only fetch template on mount, don't auto-generate
  }, [templateId, token]);

  // Regenerate AI content
  const handleRegenerate = async () => {
    setRegenerating(true);
    setError(null);
    await generateReport();
    setRegenerating(false);
    setSuccess('AI content regenerated successfully!');
    setTimeout(() => setSuccess(null), 3000);
  };

  // Toggle block expansion
  const toggleBlock = (index) => {
    setExpandedBlocks(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Handle export
  const handleExport = async (format) => {
    if (!reportData) return;

    setGenerating(true);
    setExportFormat(format);
    setError(null);
    setSuccess(null);

    try {
      if (format === 'pdf') {
        const generator = new PDFGenerator(reportData);
        await generator.generate();
        generator.download(`${template?.name?.replace(/\s+/g, '_') || 'report'}.pdf`);
      } else if (format === 'docx') {
        const generator = new DOCXGenerator(reportData);
        await generator.download(`${template?.name?.replace(/\s+/g, '_') || 'report'}.docx`);
      }
      setSuccess(`${format.toUpperCase()} exported successfully!`);
    } catch (err) {
      console.error('Export error:', err);
      setError(`Failed to export ${format.toUpperCase()}: ${err.message}`);
    } finally {
      setGenerating(false);
      setExportFormat(null);
    }
  };

  // Count AI-generated content
  const aiContentCount = reportData?.blocks?.filter(b => b.ai_content).length || 0;

  if (!currentPlan) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
              <h2 className="text-xl font-semibold mb-2">No Business Plan Selected</h2>
              <p className="text-gray-500 mb-4">
                Please select a business plan from the sidebar to generate reports.
              </p>
              <Button onClick={() => navigate('/')}>
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // STEP 1: Configuration Step - Show BEFORE generating
  if (configStep && !generating) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/reports/builder/${templateId}`)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Generate Report</h1>
              <p className="text-gray-500">Configure report settings before generating</p>
            </div>
          </div>

          {loading ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
                <p className="text-gray-500">Loading template...</p>
              </CardContent>
            </Card>
          ) : error ? (
            <Card>
              <CardContent className="py-12 text-center">
                <AlertCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
                <p className="text-gray-500 mb-4">{error}</p>
                <Button onClick={() => navigate('/reports')}>Back to Reports</Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Template Info */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    {template?.name || 'Report Template'}
                  </CardTitle>
                  <CardDescription>{template?.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{template?.blocks?.length || 0} Blocks</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">Business Plan: {currentPlan?.name}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Report Settings - BEFORE Generation */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Report Time Period
                  </CardTitle>
                  <CardDescription>
                    Configure the time period for the data included in this report
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Period Type</Label>
                      <Select
                        value={periodConfig.period_type}
                        onValueChange={(value) => setPeriodConfig(prev => ({ ...prev, period_type: value }))}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                          <SelectItem value="annual">Annual</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Year Mode</Label>
                      <Select
                        value={periodConfig.mode}
                        onValueChange={(value) => setPeriodConfig(prev => ({ ...prev, mode: value }))}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="calendar">Calendar Year (Jan-Dec)</SelectItem>
                          <SelectItem value="fiscal">Fiscal Year (Jul-Jun)</SelectItem>
                          <SelectItem value="custom">Custom Date Range</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {periodConfig.mode === 'custom' && (
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      <div className="space-y-2">
                        <Label>Start Date</Label>
                        <Input
                          type="date"
                          value={periodConfig.start_date}
                          onChange={(e) => setPeriodConfig(prev => ({ ...prev, start_date: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>End Date</Label>
                        <Input
                          type="date"
                          value={periodConfig.end_date}
                          onChange={(e) => setPeriodConfig(prev => ({ ...prev, end_date: e.target.value }))}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Generate Button */}
              <Card>
                <CardContent className="py-6">
                  <div className="text-center">
                    <p className="text-gray-600 mb-4">
                      Ready to generate your report with AI-powered content?
                    </p>
                    <Button 
                      size="lg" 
                      onClick={generateReport}
                      disabled={generating}
                      className="px-8"
                    >
                      {generating ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Generating Report...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-5 w-5 mr-2" />
                          Generate Report
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-gray-500 mt-3">
                      AI will analyze your business plan data and generate narratives, insights, and descriptions
                    </p>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    );
  }

  // STEP 2: Generating indicator
  if (generating) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardContent className="py-12 text-center">
              <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-blue-600" />
              <h2 className="text-xl font-semibold mb-2">Generating Report</h2>
              <p className="text-gray-500">
                AI is analyzing your business plan data and generating content...
              </p>
              <p className="text-sm text-gray-400 mt-2">
                This may take a moment
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // STEP 3: Preview and Export (after generation)
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => setConfigStep(true)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Report Preview</h1>
              <p className="text-gray-500">
                Review AI-generated content and export your report
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setConfigStep(true)}
            >
              <Settings className="h-4 w-4 mr-2" />
              Change Settings
            </Button>
            <Button 
              variant="outline" 
              onClick={handleRegenerate}
              disabled={regenerating}
            >
              {regenerating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Regenerate
            </Button>
          </div>
        </div>

        {error && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-2 text-red-700">
                <AlertCircle className="h-5 w-5" />
                <span>{error}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {reportData && (
          <>
            <Card className="mb-6">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-600" />
                      {template?.name || 'Report'}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {template?.description || 'No description'}
                    </CardDescription>
                  </div>
                  <Badge>{template?.audience || 'custom'}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-gray-900">
                      {reportData?.blocks?.length || 0}
                    </p>
                    <p className="text-sm text-gray-500">Blocks</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-gray-900">
                      ~{reportData?.table_of_contents?.length || 0 + 2}
                    </p>
                    <p className="text-sm text-gray-500">Pages (est.)</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="text-2xl font-bold text-amber-600 flex items-center justify-center gap-1">
                      <Sparkles className="h-5 w-5" />
                      {aiContentCount}
                    </p>
                    <p className="text-sm text-amber-700">AI Generated</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-lg font-bold text-gray-900 truncate">
                      {currentPlan?.name || 'Plan'}
                    </p>
                    <p className="text-sm text-gray-500">Business Plan</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AI Content Preview */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Report Preview
                  {aiContentCount > 0 && (
                    <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-700">
                      <Sparkles className="h-3 w-3 mr-1" />
                      {aiContentCount} AI-generated sections
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Click on blocks to expand and preview AI-generated content
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {reportData?.blocks?.map((block, index) => (
                    <div
                      key={block.id || index}
                      className={`rounded-lg border ${
                        block.ai_content 
                          ? 'border-amber-200 bg-amber-50/50' 
                          : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      {/* Block Header */}
                      <div 
                        className="flex items-center gap-3 p-3 cursor-pointer"
                        onClick={() => toggleBlock(index)}
                      >
                        <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                          block.ai_content 
                            ? 'bg-amber-100 text-amber-700' 
                            : 'bg-blue-100 text-blue-600'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 flex items-center gap-2">
                            {block.title || block.type}
                            {block.ai_content && (
                              <Sparkles className="h-4 w-4 text-amber-500" />
                            )}
                          </p>
                          <p className="text-xs text-gray-500 flex items-center gap-1 flex-wrap">
                            <span>{block.type}</span>
                            {block.data_status === 'success' && (
                              <span className="text-green-600">• Data loaded</span>
                            )}
                            {block.data_status === 'no_data' && (
                              <span className="text-amber-600">• No data available</span>
                            )}
                            {block.data_status === 'error' && (
                              <span className="text-red-600">• Error loading data</span>
                            )}
                            {!block.data_status && block.data && <span>• Data loaded</span>}
                            {block.ai_content && <span className="text-amber-600">• AI content generated</span>}
                            {block.error && !block.data_status && (
                              <span className="text-red-500">Error: {block.error}</span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {block.data && (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                          {(block.ai_content || block.data) && (
                            expandedBlocks[index] 
                              ? <ChevronUp className="h-4 w-4 text-gray-400" />
                              : <ChevronDown className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                      </div>
                      
                      {/* Expanded Content Preview */}
                      {expandedBlocks[index] && block.ai_content && (
                        <div className="px-4 pb-4 pt-0">
                          <div className="bg-white rounded-lg p-4 border border-amber-200">
                            <div className="flex items-center gap-2 text-amber-700 text-sm font-medium mb-2">
                              <Sparkles className="h-4 w-4" />
                              AI-Generated Content
                            </div>
                            <p className="text-gray-700 text-sm whitespace-pre-wrap leading-relaxed">
                              {block.ai_content}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {/* Data Preview - Tables */}
                      {expandedBlocks[index] && block.data && (block.type === 'data_section' || block.type?.startsWith('section_')) && (
                        <div className="px-4 pb-4 pt-0">
                          <div className="bg-white rounded-lg p-4 border">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2 text-blue-700 text-sm font-medium">
                                <TableProperties className="h-4 w-4" />
                                Data Preview
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {block.config?.source?.split('.').pop()?.replace(/_/g, ' ')}
                              </Badge>
                            </div>
                            {Array.isArray(block.data) && block.data.length > 0 ? (
                              <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      {Object.keys(block.data[0] || {})
                                        .filter(k => !k.startsWith('_') && typeof block.data[0][k] !== 'object')
                                        .slice(0, 5)
                                        .map(key => (
                                          <th key={key} className="px-3 py-2 text-left font-medium text-gray-700 border-b">
                                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                          </th>
                                        ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {block.data.slice(0, 5).map((row, rowIdx) => (
                                      <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        {Object.entries(row)
                                          .filter(([k, v]) => !k.startsWith('_') && typeof v !== 'object')
                                          .slice(0, 5)
                                          .map(([key, val], colIdx) => (
                                            <td key={colIdx} className="px-3 py-2 border-b text-gray-600">
                                              {typeof val === 'number' 
                                                ? val.toLocaleString() 
                                                : val !== null && val !== undefined 
                                                  ? String(val) 
                                                  : '-'}
                                            </td>
                                          ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                {block.data.length > 5 && (
                                  <p className="text-xs text-gray-500 mt-2 text-center">
                                    Showing 5 of {block.data.length} rows
                                  </p>
                                )}
                              </div>
                            ) : block.data && typeof block.data === 'object' ? (
                              <div className="space-y-2">
                                {Object.entries(block.data)
                                  .filter(([k]) => !k.startsWith('_') && k !== 'error')
                                  .slice(0, 8)
                                  .map(([key, value]) => (
                                    <div key={key} className="flex justify-between text-sm border-b pb-1">
                                      <span className="text-gray-600">
                                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                      </span>
                                      <span className="font-medium text-gray-900">
                                        {typeof value === 'number' 
                                          ? value.toLocaleString()
                                          : typeof value === 'object'
                                            ? Array.isArray(value)
                                              ? `${value.length} items`
                                              : 'Object'
                                            : value !== null && value !== undefined 
                                              ? String(value) 
                                              : '-'}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            ) : (
                              <p className="text-gray-600 text-sm">Data loaded successfully</p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Chart Preview */}
                      {expandedBlocks[index] && block.type === 'chart' && block.data && (
                        <div className="px-4 pb-4 pt-0">
                          <div className={`bg-white rounded-lg p-4 border ${
                            block.data_status === 'no_data' ? 'border-amber-200' :
                            block.data_status === 'error' ? 'border-red-200' : ''
                          }`}>
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                                <BarChart2 className="h-4 w-4" />
                                Chart Preview: {block.config?.chart_type || 'bar'} chart
                              </div>
                              {/* Data Source Badge */}
                              {block.data_source && block.data_source !== 'none' && (
                                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                                  Source: {block.data_source.replace(/_/g, ' ')}
                                </Badge>
                              )}
                            </div>
                            
                            {/* Data Status Indicator */}
                            {block.data_status === 'no_data' && (
                              <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                                <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-sm font-medium text-amber-800">No Chart Data Available</p>
                                  <p className="text-xs text-amber-700 mt-1">
                                    {block.data_message || 'This chart requires actual sales data or forecasts in your business plan.'}
                                  </p>
                                </div>
                              </div>
                            )}
                            
                            {block.data_status === 'error' && (
                              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                                <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-sm font-medium text-red-800">Error Loading Chart Data</p>
                                  <p className="text-xs text-red-700 mt-1">
                                    {block.error || 'An error occurred while fetching chart data.'}
                                  </p>
                                </div>
                              </div>
                            )}
                            
                            {block.data.labels && block.data.labels.length > 0 && block.data.datasets && block.data.datasets.length > 0 ? (
                              <div className="space-y-2">
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="bg-gray-50 p-3 rounded">
                                    <p className="text-xs text-gray-500">Labels</p>
                                    <p className="text-sm font-medium">{block.data.labels.slice(0, 5).join(', ')}{block.data.labels.length > 5 ? '...' : ''}</p>
                                  </div>
                                  <div className="bg-gray-50 p-3 rounded">
                                    <p className="text-xs text-gray-500">Data Series</p>
                                    <p className="text-sm font-medium">{block.data.datasets.length} dataset(s)</p>
                                  </div>
                                </div>
                                {block.data.datasets[0]?.data && block.data.datasets[0].data.length > 0 && (
                                  <div className="mt-2">
                                    <p className="text-xs text-gray-500 mb-1">Values</p>
                                    <div className="flex gap-1 flex-wrap">
                                      {block.data.datasets[0].data.slice(0, 6).map((val, i) => (
                                        <span key={i} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                                          {typeof val === 'number' ? val.toLocaleString() : val}
                                        </span>
                                      ))}
                                      {block.data.datasets[0].data.length > 6 && (
                                        <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded">
                                          +{block.data.datasets[0].data.length - 6} more
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p className="text-gray-500 text-sm italic">
                                {block.data_status === 'success' ? 'Chart data loaded but appears empty' : 'No chart data to display'}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Narrative/Custom Text Preview */}
                      {expandedBlocks[index] && block.type === 'narrative' && !block.ai_content && block.data?.content && (
                        <div className="px-4 pb-4 pt-0">
                          <div className="bg-white rounded-lg p-4 border">
                            <p className="text-gray-700 text-sm whitespace-pre-wrap">
                              {block.data.content.substring(0, 500)}{block.data.content.length > 500 ? '...' : ''}
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {/* Custom Table Preview */}
                      {expandedBlocks[index] && block.type === 'custom_table' && block.config?.rows && (
                        <div className="px-4 pb-4 pt-0">
                          <div className="bg-white rounded-lg p-4 border">
                            <div className="flex items-center gap-2 text-purple-700 text-sm font-medium mb-2">
                              <TableProperties className="h-4 w-4" />
                              Custom Table
                            </div>
                            <p className="text-gray-600 text-sm">
                              {block.config?.columns?.length || 0} columns × {block.config?.rows?.length || 0} rows
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Current Settings Display */}
            <Card className="mb-6 bg-blue-50 border-blue-200">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-900">Report Period</p>
                      <p className="text-sm text-blue-700">
                        {periodConfig.mode === 'calendar' ? 'Calendar Year' : periodConfig.mode === 'fiscal' ? 'Fiscal Year' : 'Custom Range'} • {periodConfig.period_type}
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setConfigStep(true)}>
                    Change Settings
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Export Options */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="h-5 w-5" />
                  Export Report
                </CardTitle>
                <CardDescription>
                  Download the report with all AI-generated content included
                </CardDescription>
              </CardHeader>
              <CardContent>
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    {error}
                  </div>
                )}
                {success && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    {success}
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    size="lg"
                    className="h-24 flex-col gap-2 bg-red-600 hover:bg-red-700"
                    onClick={() => handleExport('pdf')}
                    disabled={generating}
                  >
                    {generating && exportFormat === 'pdf' ? (
                      <Loader2 className="h-8 w-8 animate-spin" />
                    ) : (
                      <FileText className="h-8 w-8" />
                    )}
                    <span>Export as PDF</span>
                  </Button>
                  
                  <Button
                    size="lg"
                    className="h-24 flex-col gap-2 bg-blue-600 hover:bg-blue-700"
                    onClick={() => handleExport('docx')}
                    disabled={generating}
                  >
                    {generating && exportFormat === 'docx' ? (
                      <Loader2 className="h-8 w-8 animate-spin" />
                    ) : (
                      <FileSpreadsheet className="h-8 w-8" />
                    )}
                    <span>Export as Word</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportGeneratePage;
